import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Bot, TrendingUp, Clock, Calendar, CheckCircle2 } from "lucide-react";

const Skills = () => {
  const [webPages, setWebPages] = useState(5);
  const [appointments, setAppointments] = useState(100);

  // Constants for calculation
  const TRADITIONAL_WEB_COST = 800; // per page
  const OUR_WEB_COST = 250; // per page
  const webSavings = webPages * (TRADITIONAL_WEB_COST - OUR_WEB_COST);

  const ADMIN_TIME_COST_PER_APP = 12; // $12 in staff time per appointment
  const AI_BOT_COST_PER_APP = 1.5; // $1.50 in API/infra cost
  const aiSavings = appointments * (ADMIN_TIME_COST_PER_APP - AI_BOT_COST_PER_APP);

  return (
    <section id="skills" className="py-8 relative overflow-hidden bg-background">
      {/* Dynamic Background Effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-pulse/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-electric/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="font-body text-[10px] uppercase tracking-[0.4em] text-pulse bg-pulse/10 px-3 py-1 rounded-full">Value Proposition</span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
            Maximize Your <em className="text-gradient-pulse not-italic">Efficiency</em>
          </h2>
          <p className="font-body text-foreground-muted mt-6 max-w-xl mx-auto text-lg leading-relaxed">
            See exactly how much Pulse Chat can optimize your operations by replacing manual tasks with intelligent AI automation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Website Design Slider Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-[2.5rem] border border-white/5 relative group overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Globe size={120} className="text-pulse" />
            </div>

            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-2xl bg-pulse/10 flex items-center justify-center text-pulse">
                <Globe size={24} />
              </div>
              <h3 className="font-display text-2xl text-foreground">Global Scaling</h3>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex justify-between mb-4">
                  <label className="font-body text-sm text-foreground-muted uppercase tracking-widest">Expansion Scope (Nodes)</label>
                  <span className="font-display text-2xl text-pulse font-bold">{webPages}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={webPages}
                  onChange={(e) => setWebPages(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-pulse"
                />
              </div>

              <div className="pt-10 border-t border-white/5 grid grid-cols-2 gap-4">
                <div>
                  <p className="font-body text-[10px] text-foreground-muted uppercase tracking-widest mb-1">Traditional Cost</p>
                  <p className="font-display text-xl text-foreground/50 line-through">${(webPages * TRADITIONAL_WEB_COST).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-body text-[10px] text-pulse uppercase tracking-widest mb-1 font-bold">Your Investment</p>
                  <p className="font-display text-2xl text-foreground">${(webPages * OUR_WEB_COST).toLocaleString()}</p>
                </div>
              </div>

              <motion.div
                layout
                className="bg-pulse/5 border border-pulse/20 rounded-2xl p-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <TrendingUp size={20} className="text-pulse" />
                  <span className="font-body text-sm text-foreground">Total Potential Savings</span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={webSavings}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="font-display text-3xl text-pulse font-bold"
                  >
                    ${webSavings.toLocaleString()}
                  </motion.span>
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>

          {/* AI Chat Bot Slider Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 md:p-8 rounded-[2rem] border border-white/5 relative group overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Bot size={120} className="text-electric" />
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-electric/10 flex items-center justify-center text-electric">
                <Bot size={24} />
              </div>
              <h3 className="font-display text-2xl text-foreground">AI Intelligence</h3>
            </div>

            <p className="font-body text-xs text-foreground-muted mb-8 leading-relaxed">
              Pulse AI handles interactions, scheduling, and customer data with 99.9% accuracy.
            </p>

            <div className="space-y-8">
              <div>
                <div className="flex justify-between mb-4">
                  <label className="font-body text-sm text-foreground-muted uppercase tracking-widest">Monthly Interactions</label>
                  <span className="font-display text-2xl text-electric font-bold">{appointments}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="10"
                  value={appointments}
                  onChange={(e) => setAppointments(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-electric"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 pt-6">
                {[
                  { icon: Clock, text: "Instant Response" },
                  { icon: Calendar, text: "Seamless Integration" },
                  { icon: CheckCircle2, text: "99.9% Accuracy" },
                  { icon: TrendingUp, text: "Infinite Scaling" },
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 opacity-60">
                    <feature.icon size={12} className="text-electric" />
                    <span className="text-[10px] font-body uppercase tracking-wider">{feature.text}</span>
                  </div>
                ))}
              </div>

              <div className="pt-8 border-t border-white/5">
                <div className="flex flex-col items-center justify-center">
                  <p className="font-body text-xs text-foreground-muted uppercase tracking-widest mb-2 font-medium">Efficiency Score</p>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={aiSavings}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="font-display text-5xl text-electric font-black drop-shadow-[0_0_15px_rgba(255,0,242,0.4)]"
                    >
                      ${aiSavings.toLocaleString()}
                    </motion.p>
                  </AnimatePresence>
                  <div className="mt-4 px-4 py-1 rounded-full bg-electric/10 border border-electric/20">
                    <span className="text-[9px] font-bold text-electric uppercase tracking-[0.2em]">Resource optimization value</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
